import { Injectable } from "@angular/core";

import { first } from "rxjs/operators";

import { DffCurrentRoute, DffRedirectService } from "@dffedb/router";

import { UmbracoIndstillingerObserver } from "../umbraco-indstillinger";

@Injectable({ providedIn: "root" })
export class RedirectService {
    constructor(
        private readonly redirectService: DffRedirectService,
        private readonly currentRoute: DffCurrentRoute,
        private readonly indstillinger: UmbracoIndstillingerObserver
    ) {}

    public redirectTilDefault(): void {
        this.redirectService.redirect("/");
    }

    public redirectTilLogin(redirectTilAktuelleRouteEfterLogin: boolean = false): void {
        const redirectUrl = redirectTilAktuelleRouteEfterLogin ? this.currentRoute.value.path : "";
        this.redirectService.redirect(`/login${redirectUrl}`);
    }

    public redirectTilFlyttekvittering(parameter: any): void {
        this.redirectService.redirect("/meld-flytning/kvittering", { data: btoa(JSON.stringify(parameter)) });
    }

    public redirectTilInstallationer(): void {
        this.redirectService.redirect("/install-picker");
    }

    public redirectTilMitForbrug(): void {
        this.redirectService.redirect("/mit-forbrug");
    }

    public redirectTilMineDokumenter(): void {
        this.redirectService.redirect("/mine-dokumenter");
    }

    public redirect(path: string, params?: any): void {
        this.redirectService.redirect(path, params);
    }

    private removeStartUrlFromIndexedDb(): Promise<void> {
        return new Promise((resolve) => {
            if (typeof indexedDB === "undefined") return resolve();

            const DB_NAME = "vaerkslisteDB";
            const SETTINGS_STORE = "settings";
            let req: IDBOpenDBRequest;

            try {
                req = indexedDB.open(DB_NAME); // no version -> no forced upgrade
            } catch {
                return resolve();
            }

            req.onerror = () => resolve();

            req.onsuccess = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
                    db.close();
                    return resolve();
                }
                try {
                    const tx = db.transaction(SETTINGS_STORE, "readwrite");
                    const store = tx.objectStore(SETTINGS_STORE);
                    store.delete("startUrl");
                    tx.oncomplete = () => {
                        db.close();
                        resolve();
                    };
                    tx.onerror = () => {
                        db.close();
                        resolve();
                    };
                    tx.onabort = () => {
                        db.close();
                        resolve();
                    };
                } catch {
                    db.close();
                    resolve();
                }
            };
        });
    }

    /**
     * Clears all plant-related data from both localStorage and IndexedDB
     * This ensures complete cleanup when redirecting to værksliste
     */
    private clearAllPlantData(): Promise<void> {
        return new Promise((resolve) => {
            // Clear localStorage immediately
            localStorage.removeItem("chosenPlant");
            localStorage.removeItem("lastPlantSelectionTime");
            console.log("Cleared localStorage plant data");

            // Clear IndexedDB asynchronously
            this.removeStartUrlFromIndexedDb()
                .then(() => {
                    console.log("Cleared IndexedDB plant data");
                    resolve();
                })
                .catch((error) => {
                    console.error("Error clearing IndexedDB plant data:", error);
                    // Still resolve to continue with redirect even if IndexedDB fails
                    resolve();
                });
        });
    }

    public redirectTilVaerksListe(): void {
        this.indstillinger.valueChanges
            .pipe(first()) // Auto-unsubscribe
            .subscribe((indstillinger) => {
                let url = indstillinger.vaerkslisteUrl;

                // Add clear=true parameter to indicate storage should be cleared
                const separator = url.includes("?") ? "&" : "?";
                url = `${url}${separator}clear=true`;

                console.log("Redirecting to værksliste URL:", url);
                console.log("clear=true parameter added for storage management");
                location.href = url;
            });
    }
}
